import { useState, useRef, useEffect } from 'react';

type UseDebouncedValueOptions = {
  leading?: boolean;
};

export function useDebouncedValue<T>(
  value: T,
  wait: number,
  options: UseDebouncedValueOptions = { leading: false }
): [T, () => void] {
  const [_value, setValue] = useState<T>(value);
  const mountedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const cooldownRef = useRef(false);

  const cancel = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    if (mountedRef.current) {
      if (!cooldownRef.current && options.leading) {
        cooldownRef.current = true;
        setValue(value);
      } else {
        cancel();
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          setValue(value);
        }, wait);
      }
    }
  }, [value, wait, options.leading]);

  useEffect(() => {
    mountedRef.current = true;
    return cancel;
  }, []);

  return [_value, cancel];
}
